import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Network, Users, User } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Employee {
  id: string;
  first_name?: string;
  last_name?: string;
  manager_id?: string | null;
  accepted_at: string | null;
  invited_at: string;
}

interface OrgChartSummaryProps {
  employees: Employee[];
  onViewFullChart: () => void;
}

export const OrgChartSummary = ({ employees, onViewFullChart }: OrgChartSummaryProps) => {
  const { t } = useTranslation();
  const employeeIds = new Set(employees.map(e => e.id));
  
  // Count managers (employees who have direct reports)
  const managersCount = employees.filter(
    e => employees.some(emp => emp.manager_id === e.id)
  ).length;
  
  // Count root employees (no manager or manager not in list)
  const rootEmployees = employees.filter(
    e => !e.manager_id || !employeeIds.has(e.manager_id)
  );
  
  // Unassigned = root employees who don't manage anyone
  const unassignedCount = rootEmployees.filter(
    e => !employees.some(emp => emp.manager_id === e.id)
  ).length;

  // Active employees count
  const activeCount = employees.filter(e => e.accepted_at !== null).length;

  if (employees.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Network className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm sm:text-base">{t("dashboard.employees.orgChart.title", "Organization Chart")}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">
                {t("dashboard.employees.orgChart.subtitle", "Team hierarchical structure")}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="hidden sm:flex items-center gap-3">
              <div className="text-center">
                <div className="flex items-center gap-1 justify-center">
                  <Users className="h-3.5 w-3.5 text-primary" />
                  <span className="font-semibold">{managersCount}</span>
                </div>
                <span className="text-xs text-muted-foreground">{t("dashboard.employees.orgChart.managers", "Managers")}</span>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 justify-center">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-semibold">{unassignedCount}</span>
                </div>
                <span className="text-xs text-muted-foreground">{t("dashboard.employees.orgChart.independents", "Independents")}</span>
              </div>
            </div>
            
            <div className="flex sm:hidden gap-2">
              <Badge variant="secondary" className="text-xs">
                <Users className="h-3 w-3 mr-1" />
                {managersCount}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <User className="h-3 w-3 mr-1" />
                {unassignedCount}
              </Badge>
            </div>

            <Button 
              variant="outline" 
              size="sm" 
              onClick={onViewFullChart}
              className="shrink-0"
            >
              <Network className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t("dashboard.employees.orgChart.viewChart", "View org chart")}</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
